import { GetServerSideProps, NextPage } from 'next'
import { GlobalRequest } from '~/components/global-requset'
import {
  getItemListByCategory,
  getItemListByCategoryVariables,
  getItemListByCategory_marketingModule_itemList,
  getItemListByCategory_marketingModule_itemList_itemList_edges_node,
  getMallCategoryList,
  getMallCategoryListVariables,
  getMallCategoryList_marketingModule_categoryList,
  MallCategoryType,
  MallProductStatus,
} from '~/graphqls/types'
import {
  GET_ITEM_LIST_BY_CATEGORY,
  GET_MALL_CATEGORY_LIST,
} from '~/graphqls/gqls'
import { getGraphData, uniqueArray } from '~/utils'
import { Layout } from '~/layouts/default'
import { SEOHead } from '~/components/business/SEOHead'
import { Container } from '~/components/ui/Container'
import { Col, Row, message } from 'antd'
import { MallItem } from '~/components/business/MallItem'
import { useCallback, useEffect, useState } from 'react'
import { MyEmpty } from '~/components/ui/MyEmpty'
import { InfiniteScroll } from '~/components/business/InfiniteScroll'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { PageCrumbs } from '~/components/business/PageCrumbs'
import { MyTab, CateDataProps } from '~/components/ui/MyTab'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'

const PAGE_SIZE = 12

const MallCategory: NextPage<{
  categoryInfo: getItemListByCategory_marketingModule_itemList
  categoryList: getMallCategoryList_marketingModule_categoryList[]
}> = ({ categoryInfo, categoryList }) => {
  const [itemList, setItemList] = useState<
    getItemListByCategory_marketingModule_itemList_itemList_edges_node[]
  >(getGraphData(categoryInfo.itemList?.edges || []))
  const [hasNextPage, setHasNextPage] = useState(
    categoryInfo.itemList?.pageInfo?.hasNextPage || false
  )

  const [info, setInfo] = useState({
    name: categoryInfo.name,
    description: categoryInfo.description,
  })

  const [loading, setLoading] = useState(false)

  const [pageInfo, setPageInfo] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
  })

  const router = useRouter()

  const cateId = useMemo(() => {
    const id = router.query?.id as string
    return Number(id)
  }, [router.query.id])

  const initCateIndex = useMemo(() => {
    if (!cateId) return 0

    return categoryList.findIndex(item => {
      return item?.id === cateId
    })
  }, [cateId, categoryList])

  const [cateIndex, setCateIndex] = useState(initCateIndex)

  const menuData = useMemo(() => {
    return categoryList.map(it => {
      return {
        id: it.id as number,
        name: it.name as string,
      }
    }) as CateDataProps[]
  }, [categoryList])
  const getCategory = useImperativeQuery<
    getItemListByCategory,
    getItemListByCategoryVariables
  >(GET_ITEM_LIST_BY_CATEGORY)

  const onScroll = useCallback(() => {
    setLoading(true)
    getCategory({
      id: cateId,
      offset: pageInfo.pageSize * pageInfo.page,
      first: pageInfo.pageSize,
    })
      .then(data => {
        setPageInfo(page => {
          return {
            pageSize: page.pageSize,
            page: page.page + 1,
          }
        })

        setHasNextPage(
          data.data.marketingModule?.itemList?.itemList?.pageInfo
            ?.hasNextPage || false
        )

        setItemList(list => {
          return uniqueArray(
            list,
            getGraphData(
              data.data.marketingModule?.itemList?.itemList?.edges || []
            )
          )
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [cateId, pageInfo, getCategory])

  const onChangeTab = useCallback(
    (selectIndex: number) => {
      if (cateIndex === selectIndex) return
      setCateIndex(selectIndex)
      setPageInfo({
        page: 1,
        pageSize: PAGE_SIZE,
      })
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            id: menuData[selectIndex].id,
          },
        },
        undefined,
        {
          shallow: true,
        }
      )
    },
    [cateIndex]
  )

  useDidUpdateEffect(() => {
    getCategory({
      id: cateId,
      offset: 0,
      first: PAGE_SIZE,
    })
      .then(data => {
        setHasNextPage(
          data.data.marketingModule?.itemList?.itemList?.pageInfo
            ?.hasNextPage || false
        )
        setItemList(
          getGraphData(
            data.data.marketingModule?.itemList?.itemList?.edges || []
          )
        )
        setInfo({
          name: data.data.marketingModule?.itemList?.name as string,
          description: data.data.marketingModule?.itemList
            ?.description as string,
        })
      })
      .catch(err => {
        message.destroy()
        message.error('获取数据失败，请重试')
      })
  }, [cateId])

  return (
    <Layout>
      <SEOHead title={`${info.name}-文创商城`} desc={info.description} />
      <Container>
        {menuData?.length ? (
          <MyTab menu={menuData} active={cateIndex} onChange={onChangeTab} />
        ) : (
          <PageCrumbs title={categoryInfo.name || ''} />
        )}
        {itemList.length ? (
          <div className="mt-8">
            <Row gutter={32}>
              {itemList?.map(subitem => (
                <Col span={8} key={subitem.id}>
                  <MallItem data={subitem} />
                </Col>
              ))}
            </Row>
            <InfiniteScroll
              onScroll={onScroll}
              hasNextPage={hasNextPage}
              loading={loading}
            />
          </div>
        ) : (
          <MyEmpty />
        )}
      </Container>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { initialState, client } = await GlobalRequest(context)

  const cateId = Number(context.query.id)

  if (!cateId) {
    return {
      notFound: true,
    }
  }

  const categoryRes = await client.query<
    getItemListByCategory,
    getItemListByCategoryVariables
  >({
    query: GET_ITEM_LIST_BY_CATEGORY,
    variables: {
      id: cateId,
      offset: 0,
      first: PAGE_SIZE,
    },
    fetchPolicy: 'no-cache',
  })
  const categoryInfo = categoryRes?.data?.marketingModule?.itemList
  if (categoryInfo?.status !== MallProductStatus.ON) {
    return {
      notFound: true,
    }
  }
  const categoryListRes =
    categoryInfo?.type === MallCategoryType.BASE_CATEGORY
      ? await client.query<getMallCategoryList, getMallCategoryListVariables>({
          query: GET_MALL_CATEGORY_LIST,
          variables: {
            type: MallCategoryType.BASE_CATEGORY,
          },
          fetchPolicy: 'no-cache',
        })
      : null
  return {
    props: {
      initialState,
      categoryInfo: categoryInfo || {},
      categoryList:
        categoryListRes?.data?.marketingModule?.categoryList?.slice(0, 8) || [],
    },
  }
}

export default MallCategory
